<template>
  <AppHeader />
  <div class="bg-gradient-to-br from-primary-lighter via-white to-primary-lighter flex flex-col items-center justify-center">
    <!-- Hero Section -->
    <Hero 
      :regionCode="regionCode"
      :regions="regions"
      :categoryId="categoryId"
      :categories="categories"
      :loading="loading"
      :analyzingPhase="analyzingPhase"
      :clickCount="clickCount"
      @search="onSearch"
      @regionChange="onRegionChange"
      @categoryChange="onCategoryChange"
      @reset="onReset"
    />

    <!-- Main content -->
    <div ref="resultsSection">
      <ErrorState v-if="searchStarted && error" :error="error" @retry="onSearch" />

      <!-- Results sections -->
      <div
        v-else-if="searchStarted && results && !loading && !analyzingPhase"
        class="space-y-16"
      >
        <!-- Video Recommendations Section -->
        <section
          v-if="recommendations?.recommendations?.length > 0"
        >
          <VideoRecommendations
            :recommendations="recommendations"
            :loading="loadingRecommendations"
            :error="recommendationsError"
            :categoryLabel="getCategoryName(categoryId)"
            :regionLabel="getRegionName(regionCode)"
            @refresh="onRefreshRecommendations"
          />
        </section>

        <!-- Trending Videos Section -->
        <section>
          <TrendsResults
            :videos="results.videos"
            :totalVideos="results.totalVideos"
            :cacheInfo="results.cacheInfo"
            :loading="loading"
            :formatViews="formatViews"
            :formatDate="formatDate"
            :categoryLabel="getCategoryName(categoryId)"
            :regionLabel="getRegionName(regionCode)"
            @refresh="onRefresh"
          />
        </section>
      </div>

      <InitialState v-else />
    </div>
  </div>

  <!-- How It Works Section -->
  <HowItWorks />
  <Benefits />
  <FAQ />
  <AppFooter />
</template>

<script>
import AppHeader from '../../components/common/AppHeader.vue';
import AppFooter from '../../components/common/AppFooter.vue';
import Hero from '../../components/landing/Hero.vue';
import HowItWorks from '../../components/landing/HowItWorks.vue';
import Benefits from '../../components/landing/Benefits.vue';
import FAQ from '../../components/landing/FAQ.vue';
import { useTrendsAnalyzer } from '../../composables/useTrendsAnalyzer';
import TrendsResults from '../../components/trends/TrendsResults.vue';
import VideoRecommendations from '../../components/trends/VideoRecommendations.vue';
import ErrorState from '../../components/trends/ErrorState.vue';
import InitialState from '../../components/trends/InitialState.vue';
import { trendsStore } from '@/store/trends';
import { incrementVideoIdeasGenerated, getGenerationStats } from '../../composables/useUserStats';
import { auth } from '@/firebase/init';
import { mapActions } from 'vuex';

export default {
  name: 'TrendsAnalyzerPage',
  components: {
    AppHeader,
    AppFooter,
    Hero,
    HowItWorks,
    Benefits,
    FAQ,
    TrendsResults,
    VideoRecommendations,
    ErrorState,
    InitialState
  },
  data() {
    const {
      regionCode,
      regions,
      categoryId,
      categories,
      loading,
      error,
      results,
      recommendations,
      loadingRecommendations,
      recommendationsError,
      analyzeTrends,
      getRecommendations,
      formatViews,
      formatDate
    } = useTrendsAnalyzer();

    return {
      searchStarted: false,
      regionCode,
      regions,
      categoryId,
      categories,
      loading,
      error,
      results,
      recommendations,
      loadingRecommendations,
      recommendationsError,
      analyzeTrends,
      getRecommendations,
      formatViews,
      formatDate,
      minLoadingDelay: 1200,
      loadingTimer: null,
      analyzingPhase: false,
      firstSearch: true,
      // New properties for click tracking
      clickCount: 0,
      lastSearchSettings: null
    };
  },
  methods: {
    ...mapActions(['fetchUserProfile']),
    getCategoryName(id) {
      if (id === trendsStore.getters.currentCategory) {
        return trendsStore.getters.getCurrentCategoryName;
      }
      if (!id) return 'All Categories';
      const categoryList = Array.isArray(this.categories.value) ? this.categories.value : [];
      const category = categoryList.find(c => c.id === id);
      return category ? category.name : 'All Categories';
    },
    getRegionName(code) {
      if (code === trendsStore.getters.currentRegion) {
        return trendsStore.getters.getCurrentRegionName;
      }
      const regionList = Array.isArray(this.regions.value) ? this.regions.value : [];
      const region = regionList.find(r => r.code === code);
      return region ? region.name : code;
    },
    async onSearch() {
      if (!auth.currentUser) {
        this.$router.push('/login');
        return;
      }

      // Create current settings snapshot
      const currentSettings = {
        regionCode: this.regionCode,
        categoryId: this.categoryId
      };

      // Check if settings changed since last search
      const settingsChanged = this.lastSearchSettings && 
        (this.lastSearchSettings.regionCode !== currentSettings.regionCode || 
         this.lastSearchSettings.categoryId !== currentSettings.categoryId);

      // Reset click count if settings changed
      if (settingsChanged) {
        this.clickCount = 0;
      }

      // Increment click count
      this.clickCount++;

      // Store current settings for next comparison
      this.lastSearchSettings = { ...currentSettings };

      // Determine if we should force refresh (2nd+ click with same settings)
      const shouldForceRefresh = this.clickCount > 1;

      // Resolve plan key from vuex profile (globalSettings stored in store)
      const profile = this.$store.state.profile || {};
      const globalSettings = this.$store.state.globalSettings || {};
      
      // Check if plan is active first
      if (!profile?.plan?.active) {
        this.$store.dispatch('showAlert', {
          message: 'Your plan is inactive. Please reactivate your subscription to continue generating.',
          type: 'error'
        });
        return;
      }
      
      let planKey = this.mapPlanNameToKey(profile?.plan?.name);
      
      const generationLimit = (globalSettings.plans && globalSettings.plans[planKey] && globalSettings.plans[planKey].generationLimit) || null;

      // If a generation limit exists, fetch up-to-date stats and enforce it
      if (generationLimit !== null) {
        try {
          const stats = await getGenerationStats(auth.currentUser.uid);
          const currentCount = stats.count || 0;
          if (currentCount >= generationLimit) {
            // Notify user and stop
            this.$store.dispatch('showAlert', {
              message: `You have reached your ${generationLimit} generations this period. Upgrade to continue generating.`,
              type: 'error'
            });
            this.$router.push('/upgrade');
            return;
          }
        } catch (err) {
          console.error('Failed to fetch generation stats before increment:', err);
          // proceed conservatively (still allow) OR block — here we allow in case of transient error
        }
      }

      this.searchStarted = true;
      this.analyzingPhase = true;
      this.loading = true;
      if (this.loadingTimer) clearTimeout(this.loadingTimer);

      const start = Date.now();

      try {
        // Update user stats (increment) and log results
        if (auth.currentUser) {
          const result = await incrementVideoIdeasGenerated(auth.currentUser.uid);
          console.log(`✅ Video idea generation made!`);
          console.log(`📊 Total generations this period: ${result.count}`);
          console.log(`📅 Days remaining in period: ${result.daysRemaining}`);
          console.log(`🗓️ Period started: ${new Date(result.periodStartDate).toLocaleDateString()}`);
        }

        // Fetch updated user profile so Usage Statistics are fresh
        await this.fetchUserProfile();

        // Always show "Analyzing..." for at least 700ms
        await new Promise(resolve => setTimeout(resolve, 700));

        // Run trends analysis with force refresh if it's 2nd+ click
        await this.analyzeTrends(shouldForceRefresh);

        const elapsed = Date.now() - start;
        const remaining = Math.max(this.minLoadingDelay - elapsed, 300);

        // Prevent instant render glitch (especially when cache is fast)
        await new Promise(resolve => setTimeout(resolve, remaining));

        this.analyzingPhase = false;
        this.loading = false;

        // Allow Vue to update DOM before first scroll
        if (this.firstSearch) {
          await this.$nextTick();
          this.firstSearch = false;
        }

        this.scrollToResults();
      } catch (error) {
        console.error("Error during search:", error);
        this.error = error;
        this.analyzingPhase = false;
        this.loading = false;
      }
    },
    
    // New helper method to map plan names to keys
    mapPlanNameToKey(planName) {
      if (!planName) return 'free';
      
      const normalizedPlanName = planName.toLowerCase().trim();
      
      // Map common plan name variations to their keys
      if (normalizedPlanName.includes('free') || normalizedPlanName === 'free') {
        return 'free';
      }
      if (normalizedPlanName.includes('starter')) {
        return 'trendztubeStarter';
      }
      if (normalizedPlanName.includes('pro')) {
        return 'trendztubePro';
      }
      
      // Default fallback
      return 'free';
    },
    
    scrollToResults() {
      const element = this.$refs.resultsSection;
      if (element) {
        // Smooth scroll to the results section
        window.scrollTo({
          top: element.offsetTop - 100, // Offset by 100px for header
          behavior: 'smooth'
        });
      }
    },
    onRegionChange(region) {
      this.regionCode = region;
      // Reset click count when settings change
      this.clickCount = 0;
      this.lastSearchSettings = null;
    },
    onCategoryChange(category) {
      this.categoryId = category;
      // Reset click count when settings change
      this.clickCount = 0;
      this.lastSearchSettings = null;
    },
    onReset() {
      this.searchStarted = false;
      this.results = null;
      this.recommendations = null;
      this.error = null;
      this.firstSearch = true;
      // Reset click tracking
      this.clickCount = 0;
      this.lastSearchSettings = null;
    },
    onRefresh() {
      this.analyzeTrends(true);
    },
    onRefreshRecommendations() {
      this.getRecommendations(true);
    }
  }
};
</script>


<style scoped>
@keyframes spin {
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
h1 {
  text-shadow: 0 2px 16px rgba(80, 36, 255, 0.08);
}
footer {
  box-shadow: 0 -2px 24px 0 rgba(80,36,255,0.04);
}
button {
  transition: box-shadow 0.2s, transform 0.2s;
}
button:active {
  transform: scale(0.98);
  box-shadow: 0 2px 12px 0 rgba(80,36,255,0.10);
}
@keyframes loading-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>
